import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Users, 
  TrendingUp, 
  Activity,
  Building,
  BarChart3,
  Loader,
  RefreshCw,
  Calendar,
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
  X as XIcon
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';
import ReactECharts from 'echarts-for-react';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import SearchLogRow from '../shared/SearchLogRow';

const SearchAnalyticsDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    searchTerm: '',
    company: '',
    searchType: '',
    success: null,
    dateFrom: '',
    dateTo: ''
  });
  
  // Active filters count
  const activeFiltersCount = Object.values(filters).filter(v => v !== '' && v !== null).length;

  useEffect(() => {
    fetchDashboardData();
    fetchRecentSearches();
    
    // Auto-refresh every 30 seconds if enabled
    const interval = autoRefresh ? setInterval(() => {
      fetchDashboardData();
      fetchRecentSearches();
    }, 30000) : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, currentPage, itemsPerPage, filters]);

  const fetchDashboardData = async () => {
    try {
      const { data, error } = await supabase.rpc('get_search_analytics_dashboard');
      
      if (error) throw error;
      
      console.log('Dashboard data received:', data);
      console.log('Top searched parts:', data?.top_searched_parts);
      console.log('Top active companies:', data?.top_active_companies);
      console.log('Top active users:', data?.top_active_users);
      
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError(error, 'Failed to fetch dashboard data');
    }
  };

  const fetchRecentSearches = async () => {
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      
      const { data, error } = await supabase.rpc('get_search_logs', {
        p_limit: itemsPerPage,
        p_offset: offset,
        p_company_filter: filters.company || null,
        p_search_type_filter: filters.searchType || null,
        p_success_filter: filters.success,
        p_date_from: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : null,
        p_date_to: filters.dateTo ? new Date(filters.dateTo).toISOString() : null,
        p_search_term_filter: filters.searchTerm || null
      });
      
      if (error) throw error;
      
      setRecentSearches(data || []);
      if (data && data.length > 0) {
        setTotalCount(data[0].total_count);
      } else {
        setTotalCount(0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching recent searches:', error);
      showError(error, 'Failed to fetch recent searches');
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([fetchDashboardData(), fetchRecentSearches()]);
    setLoading(false);
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };
  
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      company: '',
      searchType: '',
      success: null,
      dateFrom: '',
      dateTo: ''
    });
    setCurrentPage(1);
  };
  
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getSearchVolumeChartOption = () => {
    if (!dashboardData?.daily_search_volume) return {};

    const dates = dashboardData.daily_search_volume.map(item => item.date);
    const totalSearches = dashboardData.daily_search_volume.map(item => item.total_searches);
    const successfulSearches = dashboardData.daily_search_volume.map(item => item.successful_searches);
    const failedSearches = dashboardData.daily_search_volume.map(item => item.failed_searches);

    return {
      title: {
        text: 'Search Volume Trends (Last 30 Days)',
        left: 'center',
        textStyle: { color: '#374151', fontSize: 16 }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: ['Total Searches', 'Successful', 'Failed'],
        bottom: 0
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          formatter: (value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }
      },
      yAxis: {
        type: 'value',
        name: 'Number of Searches'
      },
      series: [
        {
          name: 'Total Searches',
          type: 'line',
          data: totalSearches,
          lineStyle: { color: '#3B82F6' },
          itemStyle: { color: '#3B82F6' }
        },
        {
          name: 'Successful',
          type: 'line',
          data: successfulSearches,
          lineStyle: { color: '#10B981' },
          itemStyle: { color: '#10B981' }
        },
        {
          name: 'Failed',
          type: 'line',
          data: failedSearches,
          lineStyle: { color: '#EF4444' },
          itemStyle: { color: '#EF4444' }
        }
      ]
    };
  };

  const getSuccessRateChartOption = () => {
    if (!dashboardData?.success_rate_week) return {};

    const successRate = dashboardData.success_rate_week || 0;
    const failureRate = 100 - successRate;

    return {
      title: {
        text: 'Search Success Rate (This Week)',
        left: 'center',
        textStyle: { color: '#374151', fontSize: 16 }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c}% ({d}%)'
      },
      series: [
        {
          name: 'Search Results',
          type: 'pie',
          radius: '70%',
          data: [
            { value: successRate, name: 'Successful', itemStyle: { color: '#10B981' } },
            { value: failureRate, name: 'Failed', itemStyle: { color: '#EF4444' } }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, loading: cardLoading }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300"
    >
      <div className="p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="p-3 rounded-lg bg-gray-50">
              <Icon className={`h-6 w-6 ${color}`} />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate uppercase tracking-wide">
                {title}
              </dt>
              <dd className="text-3xl font-bold text-gray-900 mt-2">
                {cardLoading ? (
                  <div className="animate-pulse h-9 bg-gray-200 rounded w-20"></div>
                ) : (
                  value ?? '0'
                )}
              </dd>
              {subtitle && (
                <dd className="text-xs text-gray-500 mt-2 font-medium">
                  {subtitle}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
          <p className="mt-2 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Search Analytics"
        description="Customer search behavior and patterns"
        meta={lastUpdated && (
          <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
        )}
        actions={
          <>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="autoRefresh" className="text-sm text-gray-700">
                Auto-refresh
              </label>
            </div>
            <Button
              variant="secondary"
              onClick={refreshData}
              disabled={loading}
              icon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
            >
              Refresh
            </Button>
          </>
        }
      />

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Searches Today"
          value={dashboardData?.total_searches_today ?? 0}
          icon={Search}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="Active Searchers"
          value={dashboardData?.unique_searchers_today ?? 0}
          icon={Users}
          color="text-gray-500"
          subtitle="unique users today"
          loading={loading}
        />
        <StatCard
          title="Success Rate"
          value={dashboardData?.success_rate_today != null ? `${Math.round(dashboardData.success_rate_today)}%` : '0%'}
          icon={TrendingUp}
          color="text-gray-500"
          subtitle="today"
          loading={loading}
        />
        <StatCard
          title="Weekly Searches"
          value={dashboardData?.total_searches_week ?? 0}
          icon={Activity}
          color="text-gray-500"
          loading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Volume Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="p-6">
            {loading ? (
              <div className="animate-pulse h-64 bg-gray-200 rounded"></div>
            ) : dashboardData?.daily_search_volume && dashboardData.daily_search_volume.length > 0 ? (
              <ReactECharts 
                option={getSearchVolumeChartOption()} 
                style={{ height: '300px' }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No search volume data available</p>
                <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Success Rate Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="p-6">
            {loading ? (
              <div className="animate-pulse h-64 bg-gray-200 rounded"></div>
            ) : dashboardData?.success_rate_week != null ? (
              <ReactECharts 
                option={getSuccessRateChartOption()} 
                style={{ height: '300px' }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <TrendingUp className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No success rate data available</p>
                <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Top Lists Section - First Row (3 cards) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Searched Parts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              Top Searched Parts
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Most frequently searched parts this week
            </p>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {dashboardData?.top_searched_parts?.length > 0 ? dashboardData.top_searched_parts.slice(0, 10).map((part, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center min-w-0 flex-1">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-800 rounded-full text-xs font-bold mr-3 flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900 truncate">{part.search_term}</span>
                    </div>
                    <span className="text-sm text-gray-600 font-medium ml-2 flex-shrink-0">{part.count}</span>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No search data available</p>
                    <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Top Active Companies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              Most Active Companies
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Companies with highest search activity
            </p>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {dashboardData?.top_active_companies?.length > 0 ? dashboardData.top_active_companies.slice(0, 10).map((company, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center min-w-0 flex-1">
                      <Building className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-gray-900 block truncate">{company.company_name}</span>
                        <span className="text-xs text-gray-500">{company.unique_users} user{company.unique_users !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <span className="text-sm text-gray-600 font-medium ml-2 flex-shrink-0">{company.search_count}</span>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Building className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No company data available</p>
                    <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Top Active Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              Most Active Users
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Individual users with highest search activity
            </p>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {dashboardData?.top_active_users?.length > 0 ? dashboardData.top_active_users.slice(0, 10).map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center min-w-0 flex-1">
                      <Users className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-gray-900 block truncate">{user.user_name || 'Unknown User'}</span>
                        <span className="text-xs text-gray-500 truncate block">{user.company_name}</span>
                      </div>
                    </div>
                    <span className="text-sm text-gray-600 font-medium ml-2 flex-shrink-0">{user.search_count}</span>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No user data available</p>
                    <p className="text-xs text-gray-400 mt-1">Data will appear as users search</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

      </div>

      {/* Recent Activity - Full Width Row */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Recent Activity
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Latest customer searches
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border ${
                activeFiltersCount > 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
              } shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Search Term Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Term
                </label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Filter by search term..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Company Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={filters.company}
                  onChange={(e) => handleFilterChange('company', e.target.value)}
                  placeholder="Filter by company..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Search Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Type
                </label>
                <select
                  value={filters.searchType}
                  onChange={(e) => handleFilterChange('searchType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="exact">Exact Match</option>
                  <option value="fuzzy">Smart Search</option>
                  <option value="description">Description</option>
                </select>
              </div>
              
              {/* Success Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Result Status
                </label>
                <select
                  value={filters.success === null ? '' : filters.success.toString()}
                  onChange={(e) => handleFilterChange('success', e.target.value === '' ? null : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Results</option>
                  <option value="true">Successful</option>
                  <option value="false">Failed</option>
                </select>
              </div>
              
              {/* Date From Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Date To Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {activeFiltersCount > 0 && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <XIcon className="h-4 w-4 mr-2" />
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
        
        <div className="p-6">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Results Count */}
              {totalCount > 0 && (
                <div className="mb-4 text-sm text-gray-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
                </div>
              )}
              
              <div className="space-y-3">
              {recentSearches.length > 0 ? recentSearches.map((search) => (
                <SearchLogRow key={search.id} search={search} />
              )) : (
                <div className="text-center py-8">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No searches found</p>
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear filters to see all results
                    </button>
                  )}
                </div>
              )}
              </div>
              
              {/* Pagination Controls */}
              {totalCount > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                  {/* Items per page selector */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Show:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>
                  
                  {/* Pagination buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-700">
                        Page
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => {
                          const page = Number(e.target.value);
                          if (page >= 1 && page <= totalPages) {
                            handlePageChange(page);
                          }
                        }}
                        className="w-16 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm text-center"
                      />
                      <span className="text-sm text-gray-700">
                        of {totalPages}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Quick Actions
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <Eye className="mr-2 h-4 w-4" />
              View Detailed Logs
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <BarChart3 className="mr-2 h-4 w-4" />
              Generate Report
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <Calendar className="mr-2 h-4 w-4" />
              Schedule Analysis
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SearchAnalyticsDashboard;
